import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Profile } from "@/hooks/profile/types";
import { useUpdateUserProfile } from "@/hooks/admin/useUpdateUserProfile";
import { useUpdateUserEmail } from "@/hooks/admin/useUpdateUserEmail";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { addDays, isAfter, format, differenceInDays } from "date-fns";
import { es } from "date-fns/locale";
import { CalendarIcon, Loader2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

interface EditUserDialogProps {
  user: Profile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PaymentWithDate {
  id: string;
  amount: number;
  description: string | null;
  paid_at: string | null;
  validity_days: number;
  originalPaidAt: string | null;
  originalValidityDays: number;
}

export const EditUserDialog = ({ user, open, onOpenChange }: EditUserDialogProps) => {
  const [firstName, setFirstName] = useState(user.first_name || "");
  const [lastName, setLastName] = useState(user.last_name || "");
  const [email, setEmail] = useState(user.username || "");
  const [dni, setDni] = useState(user.dni || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [address, setAddress] = useState(user.address || "");
  const [emergencyPhone, setEmergencyPhone] = useState(user.emergency_phone || "");
  const [healthInsurance, setHealthInsurance] = useState(user.health_insurance || "");
  const [payments, setPayments] = useState<PaymentWithDate[]>([]);
  const [saving, setSaving] = useState(false);
  const { mutateAsync: updateProfile } = useUpdateUserProfile();
  const { mutateAsync: updateEmail } = useUpdateUserEmail();
  const queryClient = useQueryClient();

  const { data: gymData } = useQuery({
    queryKey: ["gym-name-edit-dialog", user.gym_id],
    queryFn: async () => {
      if (!user.gym_id) return null;
      const { data } = await supabase.from("gyms").select("name").eq("id", user.gym_id).maybeSingle();
      return data;
    },
    enabled: open && !!user.gym_id,
  });
  const isNewBody = !!gymData?.name?.toUpperCase().includes("NEW BODY");

  const { data: activePayments, isLoading: loadingPayments } = useQuery({
    queryKey: ["active-payments", user.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payments")
        .select("id, amount, description, paid_at, validity_days")
        .eq("user_id", user.id)
        .order("paid_at", { ascending: false });

      if (error) throw error;

      // Filter only active (non-expired) payments
      return (data || []).filter((p) => {
        if (!p.paid_at) return false;
        const expiration = addDays(new Date(p.paid_at), p.validity_days);
        return isAfter(expiration, new Date());
      });
    },
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setFirstName(user.first_name || "");
      setLastName(user.last_name || "");
      setEmail(user.username || "");
      setDni(user.dni || "");
      setPhone(user.phone || "");
      setAddress(user.address || "");
      setEmergencyPhone(user.emergency_phone || "");
      setHealthInsurance(user.health_insurance || "");
    }
  }, [open, user]);

  useEffect(() => {
    if (activePayments) {
      setPayments(
        activePayments.map((p) => ({
          ...p,
          originalPaidAt: p.paid_at,
          originalValidityDays: p.validity_days,
        }))
      );
    }
  }, [activePayments]);

  const handlePaymentDateChange = (paymentId: string, date: Date | undefined) => {
    if (!date) return;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}T00:00:00Z`;

    setPayments((prev) =>
      prev.map((p) => (p.id === paymentId ? { ...p, paid_at: dateStr } : p))
    );
  };

  const handleDaysRemainingChange = (paymentId: string, newDaysRemaining: number) => {
    setPayments((prev) =>
      prev.map((p) => {
        if (p.id !== paymentId || !p.paid_at) return p;
        const daysSincePaid = differenceInDays(new Date(), new Date(p.paid_at));
        const newValidityDays = Math.max(0, daysSincePaid + Math.max(0, newDaysRemaining));
        return { ...p, validity_days: newValidityDays };
      })
    );
  };

  const handleSave = async () => {
    if (!firstName.trim()) return;

    const isPending = user.isPending || false;
    const trimmedEmail = email.trim().toLowerCase();
    const originalEmail = (user.username || "").trim().toLowerCase();
    const emailChanged = !isPending && trimmedEmail !== originalEmail;

    if (emailChanged) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail) || trimmedEmail.length > 255) {
        toast.error("Formato de email inválido");
        return;
      }
    }

    setSaving(true);

    try {
      // Cambiar email primero (si aplica) — si falla, abortamos antes de tocar el resto
      if (emailChanged) {
        try {
          await updateEmail({ userId: user.id, newEmail: trimmedEmail });
          toast.success("Email actualizado. El usuario debe ingresar con el nuevo email.");
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Error al actualizar el email";
          toast.error(msg);
          setSaving(false);
          return;
        }
      }

      // Update profile
      await updateProfile({
        userId: user.id,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        dni: dni.trim(),
        isPending,
        phone: phone.trim(),
        address: address.trim(),
        emergencyPhone: emergencyPhone.trim(),
        healthInsurance: healthInsurance.trim(),
      });

      // Update changed payments
      const changedPayments = payments.filter(
        (p) => p.paid_at !== p.originalPaidAt || p.validity_days !== p.originalValidityDays
      );

      for (const payment of changedPayments) {
        const updatePayload: { paid_at?: string | null; validity_days?: number } = {};
        if (payment.paid_at !== payment.originalPaidAt) updatePayload.paid_at = payment.paid_at;
        if (payment.validity_days !== payment.originalValidityDays) updatePayload.validity_days = payment.validity_days;

        const { error } = await supabase
          .from("payments")
          .update(updatePayload)
          .eq("id", payment.id);

        if (error) {
          console.error("Error updating payment:", error);
          toast.error("Error al actualizar el pago");
          setSaving(false);
          return;
        }
      }

      if (changedPayments.length > 0) {
        queryClient.invalidateQueries({ queryKey: ["active-payments"] });
        queryClient.invalidateQueries({ queryKey: ["lastPayment"] });
        queryClient.invalidateQueries({ queryKey: ["user-payments"] });
      }

      if (emailChanged) {
        queryClient.invalidateQueries({ queryKey: ["users"] });
        queryClient.invalidateQueries({ queryKey: ["all-users"] });
      }

      onOpenChange(false);
    } catch (error) {
      console.error("Error saving:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md max-h-[90vh] p-0 flex flex-col gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle>Editar usuario</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="firstName">Nombre</Label>
            <Input
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              maxLength={100}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="lastName">Apellido</Label>
            <Input
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              maxLength={100}
            />
          </div>
          {!user.isPending && (
            <div className="grid gap-2">
              <Label htmlFor="email">Email de acceso</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={255}
                placeholder="usuario@ejemplo.com"
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                El usuario deberá ingresar con este email. La contraseña no cambia.
              </p>
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor="dni">DNI</Label>
            <Input
              id="dni"
              value={dni}
              onChange={(e) => setDni(e.target.value)}
              maxLength={20}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="phone">Teléfono</Label>
            <Input
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={20}
              placeholder="Número de teléfono"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="emergencyPhone">Tel. Emergencia</Label>
            <Input
              id="emergencyPhone"
              value={emergencyPhone}
              onChange={(e) => setEmergencyPhone(e.target.value)}
              maxLength={20}
              placeholder="Número de emergencia"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="address">Dirección</Label>
            <Input
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              maxLength={200}
              placeholder="Dirección"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="healthInsurance">Obra Social</Label>
            <Input
              id="healthInsurance"
              value={healthInsurance}
              onChange={(e) => setHealthInsurance(e.target.value)}
              maxLength={100}
              placeholder="Obra social"
            />
          </div>

          {/* Active payments section */}
          {loadingPayments && (
            <div className="border-t pt-4 mt-2">
              <Label className="text-sm font-semibold mb-3 block">Pagos activos</Label>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando pagos...
              </div>
            </div>
          )}
          {!loadingPayments && payments.length > 0 && (
            <div className="border-t pt-4 mt-2">
              <Label className="text-sm font-semibold mb-3 block">Pagos activos</Label>
              <div className="space-y-3">
                {payments.map((payment) => (
                  <div key={payment.id} className="flex flex-col gap-1.5 rounded-md border p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        ${payment.amount}
                        {payment.description && (
                          <span className="text-muted-foreground ml-1">
                            ({payment.description})
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground whitespace-nowrap">
                        Fecha de pago:
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal h-8 text-sm",
                              !payment.paid_at && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                            {payment.paid_at
                              ? format(new Date(payment.paid_at), "dd/MM/yyyy", { locale: es })
                              : "Seleccionar fecha"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 z-50" align="start">
                          <Calendar
                            mode="single"
                            locale={es}
                            selected={payment.paid_at ? new Date(payment.paid_at) : undefined}
                            onSelect={(date) => handlePaymentDateChange(payment.id, date)}
                            initialFocus
                            className={cn("p-3 pointer-events-auto")}
                            classNames={{
                              caption_label: "text-sm font-medium text-black",
                              head_cell: "text-black/60 rounded-md w-9 font-normal text-[0.8rem]",
                              day: "h-9 w-9 p-0 font-normal text-black hover:bg-gray-100 rounded-md",
                              day_selected: "bg-primary text-primary-foreground hover:bg-primary/80",
                              day_today: "bg-gray-100 text-black",
                              day_outside: "text-gray-400 opacity-50",
                              day_disabled: "text-gray-400 opacity-50",
                              nav_button: "h-7 w-7 bg-transparent text-black border border-gray-200 p-0 opacity-80 hover:opacity-100 inline-flex items-center justify-center rounded-md",
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    {isNewBody && payment.paid_at && (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-muted-foreground whitespace-nowrap">
                          Días restantes:
                        </Label>
                        <Input
                          type="number"
                          className="h-8 text-sm"
                          value={Math.max(
                            0,
                            differenceInDays(
                              addDays(new Date(payment.paid_at), payment.validity_days),
                              new Date()
                            )
                          )}
                          onChange={(e) =>
                            handleDaysRemainingChange(payment.id, parseInt(e.target.value) || 0)
                          }
                          min={0}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        </div>
        <DialogFooter className="px-6 py-4 border-t shrink-0 gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving || !firstName.trim()}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
